// @figma file=pkCGby54zXzTsccXvHqzO6 node=15062:12614 name="Pagination" set="mytaible-shadcn-studio" verified=2026-03-22

import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Variants ────────────────────────────────────────────────────────────────

/**
 * Visual style for PaginationLink.
 *
 * When `isActive` is true the link takes the `active` variant —
 * a bordered outline button with background token (Figma: Outline Button).
 * Otherwise it renders as a transparent ghost button.
 */
export const paginationLinkVariants = cva(
  // Base — shared with button: focus ring, disabled states, transitions
  [
    'inline-flex items-center justify-center whitespace-nowrap',
    'rounded-[var(--radius)] text-sm font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
    'cursor-pointer select-none',
  ],
  {
    variants: {
      isActive: {
        true: 'border border-border bg-background text-foreground shadow-sm',
        false: 'bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground',
      },
      size: {
        default: 'h-9 min-w-9 px-4          gap-2  [&_svg]:size-4',
        sm: 'h-8 min-w-8 px-3          gap-1.5 [&_svg]:size-3.5',
        lg: 'h-10 min-w-10 px-6        gap-2  [&_svg]:size-4',
        icon: 'h-9 w-9                          [&_svg]:size-4',
      },
    },
    defaultVariants: { isActive: false, size: 'icon' },
  },
);

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PaginationProps extends React.ComponentPropsWithoutRef<'nav'> {
  className?: string;
}

export interface PaginationContentProps extends React.ComponentPropsWithoutRef<'ul'> {
  className?: string;
}

export interface PaginationItemProps extends React.ComponentPropsWithoutRef<'li'> {
  className?: string;
}

export interface PaginationLinkProps
  extends React.ComponentPropsWithoutRef<'a'>,
    VariantProps<typeof paginationLinkVariants> {
  isActive?: boolean;
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export interface PaginationPreviousProps extends React.ComponentPropsWithoutRef<'a'> {
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

export interface PaginationNextProps extends React.ComponentPropsWithoutRef<'a'> {
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

export interface PaginationEllipsisProps extends React.ComponentPropsWithoutRef<'span'> {
  className?: string;
}

// ─── Pagination (nav root) ───────────────────────────────────────────────────

/**
 * Page navigation control providing Previous, Next, page number buttons,
 * and an ellipsis for large page ranges.
 *
 * Built entirely from native HTML elements — no Radix primitive.
 * ARIA: `role="navigation"` on the `<nav>`, `aria-label="pagination"`,
 * and `aria-current="page"` on the active PaginationLink.
 *
 * @example
 * ```tsx
 * <Pagination>
 *   <PaginationContent>
 *     <PaginationItem>
 *       <PaginationPrevious href="/page/1" />
 *     </PaginationItem>
 *     <PaginationItem><PaginationLink href="/page/1">1</PaginationLink></PaginationItem>
 *     <PaginationItem><PaginationLink href="/page/2" isActive>2</PaginationLink></PaginationItem>
 *     <PaginationItem><PaginationLink href="/page/3">3</PaginationLink></PaginationItem>
 *     <PaginationItem><PaginationEllipsis /></PaginationItem>
 *     <PaginationItem>
 *       <PaginationNext href="/page/3" />
 *     </PaginationItem>
 *   </PaginationContent>
 * </Pagination>
 * ```
 */
const Pagination = React.forwardRef<HTMLElement, PaginationProps>(
  ({ className, ...props }, ref) => (
    <nav
      ref={ref}
      aria-label="pagination"
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  ),
);
Pagination.displayName = 'Pagination';

// ─── PaginationContent ───────────────────────────────────────────────────────

const PaginationContent = React.forwardRef<HTMLUListElement, PaginationContentProps>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn('flex flex-row items-center gap-1', className)} {...props} />
  ),
);
PaginationContent.displayName = 'PaginationContent';

// ─── PaginationItem ──────────────────────────────────────────────────────────

const PaginationItem = React.forwardRef<HTMLLIElement, PaginationItemProps>(
  ({ className, ...props }, ref) => <li ref={ref} className={cn('', className)} {...props} />,
);
PaginationItem.displayName = 'PaginationItem';

// ─── PaginationLink ──────────────────────────────────────────────────────────

/**
 * Page number link. Applies `aria-current="page"` when `isActive` is true.
 *
 * @prop isActive  — Marks the currently viewed page. Renders outlined style.
 * @prop size      — Controls button dimensions (default | sm | lg | icon).
 */
const PaginationLink = React.forwardRef<HTMLAnchorElement, PaginationLinkProps>(
  ({ className, isActive = false, size = 'icon', ...props }, ref) => (
    <a
      ref={ref}
      aria-current={isActive ? 'page' : undefined}
      className={cn(paginationLinkVariants({ isActive, size }), className)}
      {...props}
    />
  ),
);
PaginationLink.displayName = 'PaginationLink';

// ─── PaginationPrevious ──────────────────────────────────────────────────────

/**
 * Previous-page control with ChevronLeft icon and visible "Previous" label.
 * Renders as a ghost-style anchor.
 */
const PaginationPrevious = React.forwardRef<HTMLAnchorElement, PaginationPreviousProps>(
  ({ className, size = 'default', ...props }, ref) => (
    <PaginationLink
      ref={ref}
      aria-label="Go to previous page"
      size={size}
      className={cn('gap-1 pl-2.5', className)}
      {...props}
    >
      <ChevronLeftIcon className="size-4" aria-hidden="true" />
      <span>Previous</span>
    </PaginationLink>
  ),
);
PaginationPrevious.displayName = 'PaginationPrevious';

// ─── PaginationNext ──────────────────────────────────────────────────────────

/**
 * Next-page control with visible "Next" label and ChevronRight icon.
 * Renders as a ghost-style anchor.
 */
const PaginationNext = React.forwardRef<HTMLAnchorElement, PaginationNextProps>(
  ({ className, size = 'default', ...props }, ref) => (
    <PaginationLink
      ref={ref}
      aria-label="Go to next page"
      size={size}
      className={cn('gap-1 pr-2.5', className)}
      {...props}
    >
      <span>Next</span>
      <ChevronRightIcon className="size-4" aria-hidden="true" />
    </PaginationLink>
  ),
);
PaginationNext.displayName = 'PaginationNext';

// ─── PaginationEllipsis ──────────────────────────────────────────────────────

/**
 * Ellipsis placeholder for hidden page ranges.
 * Renders a MoreHorizontal icon with a visually-hidden "More pages" label
 * for screen readers.
 */
const PaginationEllipsis = React.forwardRef<HTMLSpanElement, PaginationEllipsisProps>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      aria-hidden="true"
      className={cn('flex h-9 w-9 items-center justify-center text-muted-foreground', className)}
      {...props}
    >
      <MoreHorizontalIcon className="size-4" />
      <span className="sr-only">More pages</span>
    </span>
  ),
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

// ─── Exports ─────────────────────────────────────────────────────────────────

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
